// Rider presence on the QR remote, read from Users/{driverUid}.live_pax_coords.{passengerId}.
// The web writes coordinates; the native driver app owns `proximity`. Pure: no I/O, no clock.

// Fallback only: how long a missing entry or `proximity` key still means "joining" after our
// first coordinate write is acknowledged (or rejected) when no server snapshot has shown the
// rider's own entry yet (e.g. the write was refused).
export const PRESENCE_JOIN_GRACE_MS = 20000;
// How long a lock is re-checked (with fresh positions pushed) before RideEnded.
export const PRESENCE_LOCK_GRACE_MS = 90000;
// iOS skips a rider for 30 s after each verdict (striver_geo_validated): push once more just
// after that hold-off so a second verdict lands inside the grace.
export const PRESENCE_LOCK_RECHECK_MS = 31000;

export const PRESENCE = Object.freeze({
  UNKNOWN: 'unknown', // driver doc or passenger id not loaded yet
  JOINING: 'joining', // no server snapshot has shown the rider's own entry yet
  PRESENT: 'present',
  LOCKED: 'locked',
});

export const PRESENCE_VIEW = Object.freeze({
  CONNECTING: 'connecting',
  REMOTE: 'remote',
  // Lock grace for a rider who already had the remote: it stays usable, under a notice.
  RECHECKING: 'rechecking',
  // Lock grace before the rider ever had the remote (first join): the full-screen check.
  CHECKING: 'checking',
  ENDED: 'ended',
});

export const showsRemote = view => view === PRESENCE_VIEW.REMOTE || view === PRESENCE_VIEW.RECHECKING;
export const inLockGrace = view => view === PRESENCE_VIEW.RECHECKING || view === PRESENCE_VIEW.CHECKING;

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isTime = value => typeof value === 'number' && Number.isFinite(value);
const isCoordinate = isTime;

// The current lock episode: when its grace started and whether a page return has restarted it.
export const NO_LOCK = Object.freeze({ at: null, renewed: false });
export const isLock = value => isRecord(value) && isTime(value.at) && typeof value.renewed === 'boolean';

// A page return (visibility or reload) restarts the grace once per lock episode: the heartbeat
// stopped while hidden, so the verdict may have judged a stale coordinate. Later returns do not.
export function renewLock(lock, now) {
  return isLock(lock) && !lock.renewed ? { at: now, renewed: true } : lock;
}

// iOS resets live_pax_coords to [] and Android to {}; tolerate both and malformed entries.
export function ownPresenceEntry(livePax, passengerId) {
  if (!isRecord(livePax) || typeof passengerId !== 'string' || !passengerId ||
      !Object.prototype.hasOwnProperty.call(livePax, passengerId)) return null;
  return isRecord(livePax[passengerId]) ? livePax[passengerId] : null;
}

export function hasPresenceCoordinates(entry) {
  return isRecord(entry) && isCoordinate(entry.latitude) && isCoordinate(entry.longitude);
}

// The server has the rider's own entry: coordinates plus a resolved pax_update_time. The local
// echo of our pending write reads that serverTimestamp as null until Firestore acknowledges it.
export function isServerConfirmedEntry(entry) {
  return hasPresenceCoordinates(entry) && entry.pax_update_time != null;
}

// entrySeen: a snapshot has shown this rider's own server-confirmed entry. From then on the
// join grace is 0: riders scan at the kerb, where iOS writes no verdict while stopped.
export function readPresence({ loaded, passengerId, livePax, entrySeen = false, writeSettledAt = null, now }) {
  if (!loaded || !passengerId) return { state: PRESENCE.UNKNOWN, reason: 'loading' };
  const entry = ownPresenceEntry(livePax, passengerId);
  const joinOver = entrySeen ||
    (isTime(writeSettledAt) && now - writeSettledAt >= PRESENCE_JOIN_GRACE_MS);
  if (!entry) {
    return { state: joinOver ? PRESENCE.LOCKED : PRESENCE.JOINING, reason: 'absent' };
  }
  if ('expired' in entry) return { state: PRESENCE.LOCKED, reason: 'expired' };
  if (entry.proximity === true) return { state: PRESENCE.PRESENT, reason: 'verified' };
  if (entry.proximity === false) return { state: PRESENCE.LOCKED, reason: 'proximity' };
  // iOS adds `proximity` 1-3 s after the first coordinate write (never while the car is
  // stopped), Android once it has a fresh driver fix: a missing key is joining, not locked.
  // Once joined, access stays optimistic, as on master, until an explicit verdict.
  return { state: joinOver ? PRESENCE.PRESENT : PRESENCE.JOINING, reason: 'unverified' };
}

// A lock the rider can recover from by sending a fresh position. `expired` is terminal.
export function isRecheckableLock(presence, { active, override }) {
  return Boolean(active) && !override && presence.state === PRESENCE.LOCKED &&
    presence.reason !== 'expired';
}

// override: unexpired driver_secret grant. active: Users.isSessionActive.
// lockedAt: start of the current lock episode; null until recorded (counts as just locked).
// wasPresent: the rider has had the remote during this driver session (kept across a reload).
export function presenceView({ presence, override = false, active = true, lockedAt = null, wasPresent = false, now }) {
  if (override || presence.state === PRESENCE.PRESENT) return PRESENCE_VIEW.REMOTE;
  if (presence.state !== PRESENCE.LOCKED) return PRESENCE_VIEW.CONNECTING;
  if (!isRecheckableLock(presence, { active, override })) return PRESENCE_VIEW.ENDED;
  if (isTime(lockedAt) && now - lockedAt >= PRESENCE_LOCK_GRACE_MS) return PRESENCE_VIEW.ENDED;
  return wasPresent ? PRESENCE_VIEW.RECHECKING : PRESENCE_VIEW.CHECKING;
}

// Earliest future moment the derived view can change without a new snapshot, else null.
export function nextPresenceDeadline({ writeSettledAt = null, lockedAt = null, now }) {
  const deadlines = [];
  if (isTime(writeSettledAt)) deadlines.push(writeSettledAt + PRESENCE_JOIN_GRACE_MS);
  if (isTime(lockedAt)) deadlines.push(lockedAt + PRESENCE_LOCK_GRACE_MS);
  const future = deadlines.filter(at => at > now);
  return future.length ? Math.min(...future) : null;
}
